import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { CleanupBucket, CleanupItem } from '../../types';
import { ValuePill, RegexPill } from '../pills/Pills';

/**
 * Stage 7 — Detailed Cleanup Recommendations.
 *
 * Renders a Priority Bucket (header + items). 4 buckets total:
 *   🔴 CRITICAL - Must Fix Before Loading
 *   🟡 HIGH PRIORITY - Should Fix
 *   🟢 MEDIUM PRIORITY - Consider Fixing
 *   🔵 LOW PRIORITY - Optional Improvements
 */

const BUCKET_DOT = {
  critical: 'bg-cargo-error shadow-[0_0_10px_rgba(239,68,68,0.6)]',
  high: 'bg-cargo-warning shadow-[0_0_10px_rgba(251,191,36,0.6)]',
  medium: 'bg-cargo-success shadow-[0_0_10px_rgba(52,211,153,0.6)]',
  low: 'bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.6)]',
} as const;

const BUCKET_ACCENT = {
  critical: 'text-cargo-error',
  high: 'text-cargo-warning',
  medium: 'text-cargo-success',
  low: 'text-cargo-accent',
} as const;

interface PriorityBucketProps {
  bucket: CleanupBucket;
}

export const PriorityBucket: React.FC<PriorityBucketProps> = ({ bucket }) => (
  <section className="space-y-4">
    <div className="flex items-baseline gap-2.5">
      <span className={`inline-block w-3 h-3 rounded-full ${BUCKET_DOT[bucket.priority]}`} />
      <h3 className={`text-[15px] font-semibold uppercase tracking-wide ${BUCKET_ACCENT[bucket.priority]}`}>
        {bucket.title}
      </h3>
    </div>
    <div className="space-y-7 pl-1">
      {bucket.items.map((item) => (
        <CleanupRecommendationCard key={item.number} item={item} />
      ))}
    </div>
  </section>
);

/**
 * Per-item card. Universal template:
 *   N. <Column> <stats>
 *   Problem: ...
 *   • Valid: [pill] (count), ...        (when relevant)
 *   • Invalid: [pill] (count), ...      (when relevant)
 *   Cleanup Actions:
 *     <code block> OR <bullets>
 *   Recommendation: ... (with optional inline regex pill)
 */

interface CleanupRecommendationCardProps {
  item: CleanupItem;
}

export const CleanupRecommendationCard: React.FC<CleanupRecommendationCardProps> = ({ item }) => {
  const problemBullets = Array.isArray(item.problem) ? item.problem : null;

  return (
    <article className="space-y-2.5">
      {/* Heading */}
      <div className="text-cargo-text font-semibold text-[14.5px] leading-relaxed">
        {item.number}. {item.column_name}{' '}
        <span className="text-cargo-muted font-normal">{item.stats_summary}</span>
      </div>

      {/* Problem */}
      <div className="text-[13px] leading-relaxed">
        <span className="text-cargo-text font-semibold">Problem</span>
        <span className="text-cargo-muted">: </span>
        {problemBullets ? (
          <ul className="mt-1 space-y-1 ml-1">
            {problemBullets.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-cargo-muted">
                <span className="mt-2 w-1 h-1 rounded-full bg-cargo-muted flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-cargo-muted">{item.problem as string}</span>
        )}
      </div>

      {/* Valid distribution */}
      {item.valid_distribution && item.valid_distribution.length > 0 && (
        <div className="text-[13px] leading-relaxed flex flex-wrap items-baseline gap-1.5">
          <span className="text-cargo-text font-semibold">Valid</span>
          <span className="text-cargo-muted">:</span>
          {item.valid_distribution.map((v, i) => (
            <React.Fragment key={i}>
              <ValuePill value={v.value} count={v.count} />
              {i < item.valid_distribution!.length - 1 && (
                <span className="text-cargo-muted">,</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Invalid distribution */}
      {item.invalid_distribution && item.invalid_distribution.length > 0 && (
        <div className="text-[13px] leading-relaxed flex flex-wrap items-baseline gap-1.5">
          <span className="text-cargo-text font-semibold">Invalid</span>
          <span className="text-cargo-muted">:</span>
          {item.invalid_distribution.map((v, i) => (
            <React.Fragment key={i}>
              <ValuePill value={v.value} count={v.count} />
              {i < item.invalid_distribution!.length - 1 && (
                <span className="text-cargo-muted">,</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Cleanup Actions */}
      <div className="text-[13px] leading-relaxed">
        <div className="text-cargo-text font-semibold mb-1.5">Cleanup Actions:</div>
        {item.cleanup_actions.kind === 'code' ? (
          <CodeBlock
            content={item.cleanup_actions.content}
            language={item.cleanup_actions.language}
          />
        ) : (
          <ul className="space-y-1 ml-1">
            {item.cleanup_actions.items.map((bullet, i) => (
              <li key={i} className="flex items-start gap-2 text-cargo-muted">
                <span className="mt-2 w-1 h-1 rounded-full bg-cargo-muted flex-shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recommendation */}
      {item.recommendation && (
        <div className="text-[13px] leading-relaxed">
          <span className="text-cargo-text font-semibold">Recommendation</span>
          <span className="text-cargo-muted">: </span>
          <RecommendationText text={item.recommendation} />
        </div>
      )}
    </article>
  );
};

/**
 * CodeBlock — multi-line monospace, dark bg, distinct border, copy button.
 */

interface CodeBlockProps {
  content: string;
  language?: 'sql' | 'regex' | 'plain';
  className?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({
  content, language = 'plain', className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  };
  return (
    <div
      className={`
        relative group
        rounded-lg overflow-hidden
        bg-cargo-surface border border-cargo-border
        ${className}
      `}
    >
      <button
        onClick={handleCopy}
        className="
          absolute top-2 right-2 z-10
          inline-flex items-center gap-1
          px-2 py-1 rounded
          bg-cargo-panel border border-cargo-border
          text-cargo-muted text-[11px]
          opacity-0 group-hover:opacity-100
          hover:bg-cargo-hover
          transition-all
        "
        title={copied ? 'Copied!' : 'Copy'}
      >
        {copied ? <Check className="w-3 h-3 text-cargo-success" /> : <Copy className="w-3 h-3" />}
        <span>{copied ? 'Copied' : 'Copy'}</span>
      </button>
      <pre className="px-4 py-3 text-[12.5px] leading-6 text-cargo-text font-mono overflow-x-auto whitespace-pre">
        {content}
      </pre>
      {language !== 'plain' && (
        <span className="absolute bottom-1.5 right-2 text-[10px] uppercase tracking-wider text-cargo-muted font-mono">
          {language}
        </span>
      )}
    </div>
  );
};

/**
 * Render a recommendation sentence with inline regex pills detected in
 * backtick-style markers (e.g. "update pattern to ^(Male|Female)$").
 * Pure text by default; regex spans rendered as RegexPill.
 */

const RecommendationText: React.FC<{ text: string }> = ({ text }) => {
  // Match regex-looking spans: ^...$, with backticks, or with caret/dollar anchors
  const REGEX_RE = /(\^[^\s]+\$|`[^`]+`)/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = REGEX_RE.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(text.slice(lastIdx, m.index));
    }
    let val = m[0];
    if (val.startsWith('`') && val.endsWith('`')) {
      val = val.slice(1, -1);
    }
    parts.push(<RegexPill key={`re-${key++}`} value={val} />);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return <span className="text-cargo-muted">{parts.map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}</span>;
};
